import {
  AccountInfo,
  EventMessage,
  EventType,
  InteractionStatus,
} from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  createTheme,
  CssBaseline,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  ThemeProvider,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import AdminRoute from "./components/AdminRoute";
import DetailsTable from "./components/DetailsTable";
import Forecast from "./components/Forecast";
import LogViewer from "./components/LogViewer";
import LostTimeDetailsTable from "./components/LostTimeDetailsTable";
import PdfUploader from "./components/PdfUploader";
import ReportsTable from "./components/ReportsTable";
import ReportStatus from "./components/ReportStatus";
import RigReports from "./components/RigReports";
import { loginRequest } from "./config/authConfig";
import { SearchProvider, useSearch } from "./contexts/SearchContext";
import { useUser } from "./hooks/useUser";
import { AuthMonitor } from "./utils/authMonitor";

// Create a dark theme
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#ffffff",
    },
    background: {
      default: "rgb(33, 13, 42)",
      paper: "rgb(45, 25, 55)",
    },
  },
});

function UserMenu() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    instance.logoutRedirect();
  };

  // Get initials from name or username
  const getInitials = () => {
    const name = account?.name || account?.username || "";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <IconButton
        onClick={handleClick}
        size="small"
        aria-controls={open ? "account-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: "rgb(45, 25, 55)",
            color: "#ffffff",
            fontSize: "0.875rem",
            fontWeight: 500,
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          {getInitials()}
        </Avatar>
      </IconButton>
      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            bgcolor: "rgb(45, 25, 55)",
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem>
          <Typography>
            {account?.name || account?.username || "Unknown User"}
          </Typography>
        </MenuItem>
        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)" }} />
        <MenuItem onClick={handleLogout}>Sign Out</MenuItem>
      </Menu>
    </Box>
  );
}

function NavigationTabs() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useUser();
  const { selectedLocation } = useSearch();

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    navigate(newValue);
  };

  // Map root path to /reports for tab selection
  const currentPath =
    location.pathname === "/" ? "/reports" : location.pathname;

  // Disable Lost Time Details and Forecast for Abu Dhabi
  const isAbuDhabi = selectedLocation === "AbuDhabi";

  return (
    <Tabs
      value={currentPath}
      onChange={handleTabChange}
      sx={{
        flexGrow: 1,
        display: "flex",
        justifyContent: "center",
        "& .MuiTab-root": {
          color: "rgba(255, 255, 255, 0.7)",
          "&.Mui-selected": {
            color: "white",
          },
          "&.Mui-disabled": {
            color: "rgba(255, 255, 255, 0.3)",
          },
        },
      }}
    >
      <Tab label="Reports" value="/reports" />
      <Tab label="Lost Time Details" value="/lost-time-details" disabled={isAbuDhabi} />
      <Tab label="Forecast" value="/forecast" disabled={isAbuDhabi} />
      <Tab label="Upload" value="/upload" disabled={!isAdmin} />
      <Tab label="Status" value="/status" disabled={!isAdmin} />
      <Tab label="Logs" value="/logs" disabled={!isAdmin} />
    </Tabs>
  );
}

function ReportsTableWrapper() {
  return <ReportsTable />;
}

function DetailsTableWrapper() {
  return <DetailsTable />;
}

function LostTimeDetailsTableWrapper() {
  return <LostTimeDetailsTable />;
}

function ProtectedRoute({ children }: { children: JSX.Element }) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const [loginInitiated, setLoginInitiated] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Single place to initiate login redirect
  useEffect(() => {
    if (
      !isAuthenticated &&
      inProgress === InteractionStatus.None &&
      !loginInitiated
    ) {
      setLoginInitiated(true);
      instance.loginRedirect(loginRequest).catch((err) => {
        console.error("Login redirect failed", err);
        setAuthError(err.message || "Authentication failed");
        setLoginInitiated(false);
      });
    }
  }, [isAuthenticated, inProgress, loginInitiated, instance]);

  // Event handling to clear errors/state
  useEffect(() => {
    const id = instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        setAuthError(null);
        setLoginInitiated(false);
      }
      if (event.eventType === EventType.LOGIN_FAILURE) {
        setAuthError("Login failed. Please try again.");
        setLoginInitiated(false);
      }
    });
    return () => {
      if (id) instance.removeEventCallback(id);
    };
  }, [instance]);

  if (authError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          textAlign: "center",
          p: 3,
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Authentication Error
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {authError}
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            setAuthError(null);
            setLoginInitiated(false);
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <Typography>Authenticating...</Typography>
      </Box>
    );
  }

  return children;
}

function App() {
  const { instance } = useMsal();
  const [authMonitor, setAuthMonitor] = useState<AuthMonitor | null>(null);

  useEffect(() => {
    if (!instance.getActiveAccount() && instance.getAllAccounts().length > 0) {
      instance.setActiveAccount(instance.getAllAccounts()[0]);
    }

    // Initialize the auth monitor
    const monitor = new AuthMonitor(instance);
    setAuthMonitor(monitor);

    const eventCallback = (event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        if ("account" in event.payload && event.payload.account) {
          const account = event.payload.account as AccountInfo;
          instance.setActiveAccount(account);
        }
      }
    };

    instance.addEventCallback(eventCallback);

    // Cleanup on unmount
    return () => {
      if (monitor) {
        monitor.destroy();
      }
    };
  }, [instance]);

  return (
    <ThemeProvider theme={darkTheme}>
      <SearchProvider>
        <Router>
          <ProtectedRoute>
            <>
              <Helmet>
                <meta
                  httpEquiv="Content-Security-Policy"
                  content="default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; font-src 'self' data:; connect-src 'self' https://localhost:7001 http://localhost:5000 https://localhost:5000 https://login.microsoftonline.com; frame-src 'self' https://login.microsoftonline.com;"
                />
              </Helmet>
              <CssBaseline />
              <AppBar position="static" sx={{ bgcolor: "rgb(33, 13, 42)" }}>
                <Toolbar>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ width: "100px" }}
                  >
                    RigAI
                  </Typography>
                  <NavigationTabs />
                  <UserMenu />
                </Toolbar>
              </AppBar>
              <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
                <Routes>
                  <Route path="/upload" element={<PdfUploader />} />
                  <Route path="/reports" element={<ReportsTableWrapper />} />
                  <Route
                    path="/lost-time-details"
                    element={<LostTimeDetailsTableWrapper />}
                  />
                  <Route path="/forecast" element={<Forecast />} />
                  <Route path="/status" element={<ReportStatus />} />
                  <Route
                    path="/logs"
                    element={
                      <AdminRoute>
                        <LogViewer />
                      </AdminRoute>
                    }
                  />
                  <Route path="/rig/:rigName" element={<RigReports />} />
                  <Route path="/" element={<ReportsTableWrapper />} />
                </Routes>
              </Container>
            </>
          </ProtectedRoute>
        </Router>
      </SearchProvider>
    </ThemeProvider>
  );
}

export default App;
